import { Badge, Button, type ContextMenuItem, IconTile, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  errorMessage,
  listQuickActionsOptions,
  listQuickActionsQueryKey,
  type SpaceQuickActionSummary,
  spacesCreateQuickActionMutation,
  spacesDeleteQuickActionMutation,
  spacesUpdateQuickActionMutation,
} from "@/api";
import { EntityRow } from "@/components/build/EntityRow";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { ASSISTANT, QuickActionModal, type QuickActionValues } from "./QuickActionModal";
import { useRefreshSpace, useSettingsSpace } from "./useSpaceSettings";

/** Quick actions for the space: always shared; add, edit, turn off, reorder, delete. */
export function QuickActionsSection() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.quickActions" });
  const space = useSettingsSpace();
  const toast = useToast();
  const queryClient = useQueryClient();
  const refreshSpace = useRefreshSpace(space.uuid);
  const path = { space_uuid: space.uuid };
  const actions = useQuery({ ...listQuickActionsOptions({ path }), select: (r) => r.data });
  const [editing, setEditing] = useState<SpaceQuickActionSummary | "new" | null>(null);
  const refresh = () =>
    Promise.all([queryClient.invalidateQueries({ queryKey: listQuickActionsQueryKey({ path }) }), refreshSpace()]);
  const onError = (error: unknown) => toast.error(errorMessage(error, t("failed")));
  const create = useMutation({ ...spacesCreateQuickActionMutation(), onSettled: refresh });
  const update = useMutation({ ...spacesUpdateQuickActionMutation(), onSettled: refresh, onError });
  const remove = useMutation({
    ...spacesDeleteQuickActionMutation(),
    onSettled: refresh,
    onError,
    onSuccess: () => toast.success(t("deleted")),
  });

  const patch = (a: SpaceQuickActionSummary, body: Record<string, unknown>) =>
    update.mutate({ path: { ...path, action_uuid: a.uuid }, body });
  const save = (values: QuickActionValues) => {
    const body = {
      label: values.label,
      description: values.description || null,
      agentUuid: values.agentUuid === ASSISTANT ? null : values.agentUuid,
      promptTemplate: values.promptTemplate || null,
      autoSubmit: values.autoSubmit,
    };
    if (editing && editing !== "new") return update.mutateAsync({ path: { ...path, action_uuid: editing.uuid }, body });
    return create.mutateAsync({
      path,
      body: { ...body, actionType: "agent", displayOrder: (actions.data?.length ?? 0) + 1 },
    });
  };
  /** Swap display orders with the neighbor (orders are renumbered from the list). */
  const move = (list: SpaceQuickActionSummary[], index: number, by: -1 | 1) => {
    const other = list[index + by];
    if (!other) return;
    patch(list[index], { displayOrder: index + by });
    patch(other, { displayOrder: index });
  };

  const menu = (list: SpaceQuickActionSummary[], a: SpaceQuickActionSummary, i: number): ContextMenuItem[] => [
    { label: t("edit"), onSelect: () => setEditing(a) },
    {
      label: a.isEnabled === false ? t("turnOn") : t("turnOff"),
      onSelect: () => patch(a, { isEnabled: a.isEnabled === false }),
    },
    { label: t("moveUp"), disabled: i === 0, onSelect: () => move(list, i, -1) },
    { label: t("moveDown"), disabled: i === list.length - 1, onSelect: () => move(list, i, 1) },
    { label: t("delete"), danger: true, onSelect: () => remove.mutate({ path: { ...path, action_uuid: a.uuid } }) },
  ];

  return (
    <Page title={t("title")}>
      <div className="flex items-start justify-between gap-3">
        <Text variant="body-sm" tone="secondary" className="max-w-xl">
          {t("hint")}
        </Text>
        <Button variant="secondary" size="sm" onClick={() => setEditing("new")}>
          {t("add")}
        </Button>
      </div>
      <QuerySection
        query={actions}
        isEmpty={(list) => list.length === 0}
        empty={{ icon: <Zap size={18} />, hint: t("empty") }}
      >
        {(list) => (
          <ul className="flex flex-col gap-2">
            {list.map((a, i) => (
              <li key={a.uuid}>
                <EntityRow
                  icon={
                    <IconTile>
                      <Zap size={18} />
                    </IconTile>
                  }
                  name={a.label}
                  description={[
                    a.agentName && t("agentLabel", { agent: a.agentName }),
                    a.canUse === false && t("noAccess", { agent: a.agentName ?? "" }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  meta={a.isEnabled === false && <Badge variant="neutral">{t("off")}</Badge>}
                  onOpen={() => setEditing(a)}
                  menuItems={menu(list, a, i)}
                />
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
      {editing && (
        <QuickActionModal
          action={editing === "new" ? undefined : editing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </Page>
  );
}
